import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { Agreement } from "@modules/agreements/types";
import { ModalRef } from "@shared/components";
import { useGenerateAgreementMutation } from "@modules/agreements/services/mutations";

export function useAgreementsList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [cloneTarget, setCloneTarget] = useState<{
    id: number | string;
    name: string;
  } | null>(null);

  const generateTokenModalRef = useRef<ModalRef>(null);
  const cloneModalRef = useRef<ModalRef>(null);
  const { mutate: generateToken } = useGenerateAgreementMutation();

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const handleCloseTokenModal = () => {
    setCurrentToken(null);
    setTokenError(null);
    generateTokenModalRef?.current?.close();
  };

  const handleGenerateToken = (agreementId: string) => {
    setIsGenerating(true);
    setTokenError(null);
    generateTokenModalRef.current?.open();

    generateToken({ id: agreementId }, {
      onSuccess: (data) => {
        setCurrentToken(data.token);
      },
      onError: (error) => {
        setTokenError(error.response?.data?.error || translate("v1/agreement_admin.errors.token_generation_failed"));
      },
      onSettled: () => {
        setIsGenerating(false);
      }
    });
  };

  const handleCloneAgreement = (agreementId: number | string, name: string) => {
    setCloneTarget({ id: agreementId, name });
  };

  const handleCloseCloneModal = () => {
    setCloneTarget(null);
  };

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<Agreement>({
    resource: "v1/agreement_admin",
    filters: getQueryParams(),
  });

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows: rows || [],
    loading,
    tableProps,
    selectedRows,
    generateTokenModalRef,
    cloneModalRef,
    handleGenerateToken,
    handleCloneAgreement,
    handleCloseTokenModal,
    handleCloseCloneModal,
    cloneTarget,
    currentToken,
    isGenerating,
    tokenError,
    dataToExport,
    refetch,
    setSelectedRows,
    translate,
  };
}
